import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import type { StatCategory } from '@sorare/core/src/__generated__/globalTypes';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useToggle } from '@sorare/core/src/hooks/useToggle';

import { isCategoryDecisive } from '#src/shared/stats/PlayerGameScoreStats/utils.ts';

import { CategoryHeader } from './CategoryHeader/index.tsx';
import { CategoryStat } from './CategoryStat/index.tsx';
import type {
  Category_playerGameScoreInterface,
  Category_statScore,
} from './__generated__/index.graphql.ts';

type Props = {
  category: StatCategory;
  playerGameScores: Category_playerGameScoreInterface[];
  stats: Category_statScore[];
  defaultExpanded?: boolean;
};

const Wrapper = styled.div`
  background-color: var(--c-nd-100-opaque);
`;

const StatsList = styled(Vertical)`
  padding: var(--half-unit) var(--double-unit) var(--double-unit);
`;

export const Category = ({
  category,
  playerGameScores,
  stats,
  defaultExpanded,
}: Props) => {
  const [expanded, toggleExpanded] = useToggle(!!defaultExpanded);
  const isDecisive = isCategoryDecisive(category);
  const nonZeroStats = stats.filter(stat => stat.statValue !== 0);

  return (
    <Wrapper>
      <CategoryHeader
        category={category}
        playerGameScores={playerGameScores}
        expanded={expanded}
        onClick={toggleExpanded}
      />
      {!!isDecisive && !expanded && nonZeroStats.length > 0 && (
        <StatsList>
          {nonZeroStats.map(stat => (
            <CategoryStat
              key={stat.stat}
              category={category}
              stat={stat}
              playerGameScores={playerGameScores}
            />
          ))}
        </StatsList>
      )}

      <Collapse open={expanded} className={classNames({ expanded })} gap={0}>
        <StatsList>
          {stats.map(stat => (
            <CategoryStat
              key={stat.stat}
              category={category}
              stat={stat}
              playerGameScores={playerGameScores}
            />
          ))}
        </StatsList>
      </Collapse>
    </Wrapper>
  );
};

Category.fragments = {
  playerGameScoreInterface: gql`
    fragment Category_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      ...CategoryHeader_playerGameScoreInterface
      ...CategoryStat_playerGameScoreInterface
    }
    ${CategoryHeader.fragments.playerGameScoreInterface}
    ${CategoryStat.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<Category_playerGameScoreInterface>,
  statScore: gql`
    fragment Category_statScore on StatScore {
      statValue
      ...CategoryStat_statScore
    }
    ${CategoryStat.fragments.statScore}
  ` as TypedDocumentNode<Category_statScore>,
};
